// typescript
import React from "react";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import CharCounter from "@/main/react/component/CharCounter";
import { FloatingLabel } from "@/main/react/component/FloatingLabel";

/**
 * `SETTINGS-69`. How close to `maxLength` the polite live region starts speaking. Below this it renders
 * empty and announces nothing, which is what keeps it from narrating every keystroke.
 */
const LIVE_REGION_WARN_REMAINING = 20;

/**
 * The near-limit announcement. Rendered UNCONDITIONALLY so the live region already exists when it has
 * something to say — a region inserted at the same moment as its text is announced far less reliably,
 * the same shape `InviteSomeoneForm` and `ChangeUsernameForm` already use for their error regions.
 */
function LimitAnnouncement({ active, length, max }: { active: boolean; length: number; max?: number }) {
    return (
        <span role="status" aria-live="polite" className="sr-only" data-playwright-id="char-counter-limit-announcement">
            {active && typeof max === "number" ? `${length}/${max}` : ""}
        </span>
    );
}

type Props = React.ComponentProps<typeof Textarea> & {
    onEnter?: () => void;
    wrapperClassName?: string;
    /**
     * When provided, renders a Reddit/Facebook-style floating label (the multi-line sibling of
     * `InputWithCharCounter`): the (already-localized) text rests at the first line like a
     * placeholder and floats to a small label on focus or once the field has a value. The
     * character counter is a compact line right-aligned BELOW the field (`GROUP-38`; it used to sit
     * inside the field and paint over its text). Requires a stable `id` so the
     * label's `htmlFor` resolves.
     */
    floatingLabel?: string;
    /** Fully suppress the character counter. */
    hideCharCounter?: boolean;
};

export const TextAreaWithCharCounter = React.forwardRef<HTMLTextAreaElement, Props>(function TextAreaWithCharCounter({
                                                             onEnter,
                                                             wrapperClassName,
                                                             floatingLabel,
                                                             hideCharCounter = false,
                                                             ...props
                                                         }, ref) {
    const initialLength = (() => {
        if (props.value != null) return String(props.value).length;
        if (props.defaultValue != null) return String(props.defaultValue).length;
        return 0;
    })();

    const [length, setLength] = React.useState<number>(initialLength);
    const [focused, setFocused] = React.useState(false);

    /*
      `SETTINGS-69`, 2026-08-23. This component shipped with ZERO `aria-*` attributes. The character
      counter was decorative text: no `id`, no live region and no link to the field it counts, while
      `maxLength` silently stops the browser accepting keystrokes at 1,000 and 280 characters on the two
      profile fields. A screen-reader member got no warning near the limit and no signal when the field
      stopped responding — it simply went quiet.

      Two things are wired here, and they are separate mechanisms:

        1. The counter joins the textarea's `aria-describedby`, so its value is reachable ON DEMAND at
           any length. It MERGES with a caller-supplied `aria-describedby` rather than replacing it —
           `UpdateProfileSettings` passes the statement field's helper line through that prop, and
           silently dropping it would have traded one orphaned sentence for another.
        2. A polite live region announces the count only inside the last `LIVE_REGION_WARN_REMAINING`
           characters. That threshold is the whole design: a region that speaks on every keystroke is
           one a member turns off, and the row's Direction asks for one that "speaks only near the
           limit". Away from the limit the region renders empty and announces nothing.

      ⚠️ It announces the counter's own NUMERIC form (`980/1000`), not a sentence, because minting copy
      is a 25-locale bill and no batch but the catalog owner may add a key. A sentence would be the
      better announcement; that is recorded as an open question in the wave-9 batch-F doc, not silently
      decided here.

      The label side is untouched on purpose: this control already HAS a real accessible name (the
      floating label, or the wrapping `<label>` in the plain branch), so `aria-labelledby` would replace
      it rather than extend it — which is why this file takes `describedby` where the switch rows in
      `SafetyControlsForm` and `UpdateProfileSettings` take `labelledby`.
    */
    const counterId = React.useId();
    const hasMax = typeof props.maxLength === "number";
    const remaining = hasMax ? (props.maxLength as number) - length : Number.POSITIVE_INFINITY;
    const nearLimit = hasMax && remaining <= LIVE_REGION_WARN_REMAINING;
    const describedBy = [props["aria-describedby"], hideCharCounter ? null : counterId]
        .filter(Boolean)
        .join(" ");

    React.useEffect(() => {
        if (props.value != null) {
            setLength(String(props.value).length);
        }
    }, [props.value]);

    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
        setLength(e.target.value.length);
        props.onChange?.(e);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
        const nativeEvent = e.nativeEvent as KeyboardEvent & {isComposing?: boolean};
        const shouldHandleEnter = e.key === "Enter"
            && !e.shiftKey
            && !e.altKey
            && !e.ctrlKey
            && !e.metaKey
            && !nativeEvent.isComposing;

        if (shouldHandleEnter && onEnter) {
            e.preventDefault();
            onEnter();
            return;
        }

        props.onKeyDown?.(e);
    };

    const forwarded: React.ComponentProps<typeof Textarea> = {
        ...props,
        "aria-describedby": describedBy || undefined,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
        onFocus: (event) => {
            setFocused(true);
            props.onFocus?.(event);
        },
        onBlur: (event) => {
            setFocused(false);
            props.onBlur?.(event);
        },
    };

    // With a floating label, keep the compact counter out of the way until the field is in use
    // (focused or has content), matching InputWithCharCounter.
    const showCharCounter = !hideCharCounter && (floatingLabel == null || focused || length > 0);

    if (floatingLabel) {
        return (
            <div className={`relative ${wrapperClassName ?? ""}`}>
                <Textarea
                    {...forwarded}
                    ref={ref}
                    placeholder=" "
                    // Reserve top room for the floated label.
                    className={cn(forwarded.className, "peer pt-6")}
                />
                <FloatingLabel htmlFor={props.id} variant="textarea">{floatingLabel}</FloatingLabel>
                {/* `GROUP-38` (A35, 2026-09-28): the counter is a static line BELOW the field. It used to
                    sit `absolute bottom-2 right-3` inside it over a `pb-7` reservation, which a
                    `max-h-*` scrolling textarea does not honour: the counter was drawn over the field's
                    own text and the last visible line was sliced by the box edge. */}
                {showCharCounter ? (
                    <div id={counterId}>
                        <CharCounter
                            length={length}
                            min={props.minLength}
                            max={props.maxLength}
                            compact
                            className="mt-1 pe-3"
                        />
                    </div>
                ) : null}
                <LimitAnnouncement
                    active={nearLimit && !hideCharCounter}
                    length={length}
                    max={props.maxLength}
                />
            </div>
        );
    }

    return (
        <div className={`relative ${wrapperClassName ?? ""}`}>
            <label className="form__label" htmlFor={props.name}>
                <Textarea {...forwarded} ref={ref} />
            </label>
            {!hideCharCounter ? (
                <div id={counterId}>
                    <CharCounter length={length} min={props.minLength} max={props.maxLength} />
                </div>
            ) : null}
            <LimitAnnouncement
                active={nearLimit && !hideCharCounter}
                length={length}
                max={props.maxLength}
            />
        </div>
    );
});

export default TextAreaWithCharCounter;
